import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Download, Loader2, RefreshCw } from "lucide-react";

/**
 * BhCustomerImportDialog — Satış personelinin Cariler sayfasından
 * Bizim Hesap staging listesindeki carileri seçip Operix'e aktarmasını sağlar.
 *
 * Backend endpoint'leri REUSE (yeni yaratılmadı):
 *   GET  /api/bizim-hesap/customers            — staging list (search)
 *   POST /api/bizim-hesap/import/customers/preview
 *   POST /api/bizim-hesap/import/customers
 *
 * Yetki (backend gate'ler): customers.add.
 * Bu bileşen mount olduğunda GET çağrısı yapıldığından dialog sadece
 * yetkili kullanıcılar için render edilmelidir (parent'ta `can()` ile gate'le).
 */
export default function BhCustomerImportDialog({ open, onClose, onImported }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [asSupplier, setAsSupplier] = useState(false);

  const load = async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bizim-hesap/customers?search=${encodeURIComponent(search)}&limit=300`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setPreview(null);
      setQ("");
      load("");
    }
  }, [open]);

  // Debounce search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q, open]); // eslint-disable-line

  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.bh_id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allChecked) rows.forEach((r) => n.delete(r.bh_id));
    else rows.forEach((r) => n.add(r.bh_id));
    return n;
  });

  const openPreview = async (targetIsSupplier = asSupplier) => {
    const ids = [...selected];
    if (!ids.length) { toast.error("En az bir cari seçin"); return; }
    setAsSupplier(targetIsSupplier);
    try {
      const { data } = await api.post("/bizim-hesap/import/customers/preview", { bh_ids: ids, as_supplier: targetIsSupplier });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doImport = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const ids = (preview.items || []).filter((r) => r.status === "new" || r.status === "matched").map((r) => r.bh_id);
      if (!ids.length) { toast.error("Aktarılacak cari yok (tümü zaten aktarılmış veya hatalı)"); setBusy(false); return; }
      const { data } = await api.post("/bizim-hesap/import/customers", { bh_ids: ids, as_supplier: asSupplier });
      const c = data.counts || {};
      toast.success(`${asSupplier ? "Tedarikçi" : "Cari"} aktarım tamam · Yeni: ${c.created} · Eşleşme: ${c.matched} · Atlanan: ${c.skipped} · Hata: ${c.error}`);
      setPreview(null);
      setSelected(new Set());
      onImported?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const previewCounts = useMemo(() => {
    if (!preview?.items) return { new: 0, matched: 0, already: 0, missing: 0, error: 0 };
    return preview.items.reduce((acc, r) => {
      const k = r.status === "already_linked" ? "already" : r.status;
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
  }, [preview]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-5xl bg-slate-900 border-slate-800" data-testid="bh-customer-import-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight">
            Bizim Hesap'tan {asSupplier ? "Tedarikçi" : "Cari"} Aktar
          </DialogTitle>
        </DialogHeader>

        {!preview && (
          <div className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest" data-testid="bh-import-kind-tabs">
            <button type="button" data-testid="bh-import-kind-customer"
              onClick={() => setAsSupplier(false)}
              className={`px-3 py-1.5 rounded-sm border ${!asSupplier ? "border-blue-500 bg-blue-500/15 text-blue-300" : "border-slate-800 text-slate-500 hover:text-slate-300"}`}>
              Müşteri
            </button>
            <button type="button" data-testid="bh-import-kind-supplier"
              onClick={() => setAsSupplier(true)}
              className={`px-3 py-1.5 rounded-sm border ${asSupplier ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-800 text-slate-500 hover:text-slate-300"}`}>
              Tedarikçi
            </button>
            <span className="text-[10px] text-slate-600 ml-2">
              (Bizim Hesap müşteri/tedarikçi ayrımı sağlamıyor — sekmeye göre hedef koleksiyon değişir)
            </span>
          </div>
        )}

        {!preview && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  data-testid="bh-import-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari adı / kod / vergi no / telefon..."
                  className="pl-10 rounded-sm bg-slate-950 border-slate-800"
                />
              </div>
              <Button
                data-testid="bh-import-reload"
                variant="outline"
                onClick={() => load(q)}
                className="rounded-sm border-slate-800"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>

            <div className="text-[11px] text-slate-500">
              Aktarılacak carileri seçin. Yalnızca son senkronizasyondaki liste görünür. Duplicate kontrol otomatiktir (vergi no / e-posta / telefon / normalize isim).
            </div>

            {selected.size > 0 && (
              <div
                className="sticky top-0 z-10 flex flex-col sm:flex-row sm:items-center gap-2 rounded-sm border border-blue-900/60 bg-slate-950/95 px-3 py-2"
                data-testid="bh-import-action-bar"
              >
                <div className="text-xs text-slate-300 mr-auto" data-testid="bh-import-selected-count">
                  {selected.size} cari seçildi
                </div>
                <Button
                  data-testid="bh-import-as-customer-btn"
                  onClick={() => openPreview(false)}
                  className="bg-blue-600 hover:bg-blue-500 rounded-sm"
                >
                  <Download className="w-4 h-4 mr-2" /> Müşteri Olarak Aktar
                </Button>
                <Button
                  data-testid="bh-import-as-supplier-btn"
                  onClick={() => openPreview(true)}
                  className="bg-amber-600 hover:bg-amber-500 rounded-sm"
                >
                  <Download className="w-4 h-4 mr-2" /> Tedarikçi Olarak Aktar
                </Button>
              </div>
            )}

            <div className="max-h-[420px] overflow-auto border border-slate-800 rounded-sm">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-950/95">
                  <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" data-testid="bh-import-select-all" checked={allChecked} onChange={toggleAll} />
                    </th>
                    <th className="text-left px-3 py-2">Kod</th>
                    <th className="text-left px-3 py-2">Ünvan</th>
                    <th className="text-left px-3 py-2">Vergi No / Daire</th>
                    <th className="text-left px-3 py-2">Telefon</th>
                    <th className="text-left px-3 py-2">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {loading && rows.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Yükleniyor…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-500">Kayıt bulunamadı. Bizim Hesap senkronu son güncellendiğinde staging'de bulunan cariler burada listelenir.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.bh_id} className={`text-slate-300 hover:bg-slate-900/40 ${selected.has(r.bh_id) ? "bg-blue-950/20" : ""}`} data-testid={`bh-import-row-${r.bh_id}`}>
                      <td className="px-3 py-2"><input type="checkbox" data-testid={`bh-import-select-${r.bh_id}`} checked={selected.has(r.bh_id)} onChange={() => toggle(r.bh_id)} disabled={!!(asSupplier ? r.linked_supplier_id : r.linked_erp_id)} /></td>
                      <td className="px-3 py-2 font-mono text-[11px]">{r.code || "—"}</td>
                      <td className="px-3 py-2">{r.title}</td>
                      <td className="px-3 py-2 text-slate-400">
                        {r.tax_no || "—"}
                        {r.tax_office ? <span className="text-slate-500"> · {r.tax_office}</span> : null}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px]">{r.phone || "—"}</td>
                      <td className="px-3 py-2 text-[10px]">
                        {(asSupplier ? r.linked_supplier_id : r.linked_erp_id)
                          ? <span className="text-emerald-400">✓ Aktarılmış</span>
                          : <span className="text-slate-500">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter className="pt-2">
              <div className="text-xs text-slate-400 mr-auto">{selected.size} cari seçildi</div>
              <Button variant="ghost" onClick={onClose} className="rounded-sm">İptal</Button>
              <Button
                data-testid="bh-import-preview-btn"
                onClick={openPreview}
                disabled={selected.size === 0}
                className="bg-blue-600 hover:bg-blue-500 rounded-sm"
              >
                <Download className="w-4 h-4 mr-2" /> Aktarımı Önizle ({selected.size})
              </Button>
            </DialogFooter>
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-xs">
              <PreviewStat label="Yeni" value={previewCounts.new || 0} tone="emerald" />
              <PreviewStat label="Eşleşen" value={previewCounts.matched || 0} tone="blue" />
              <PreviewStat label="Zaten Var" value={previewCounts.already || 0} tone="slate" />
              <PreviewStat label="Eksik" value={previewCounts.missing || 0} tone="amber" />
              <PreviewStat label="Hata" value={previewCounts.error || 0} tone="rose" />
            </div>
            <div className="max-h-[400px] overflow-auto border border-slate-800 rounded-sm">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-950/95">
                  <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
                    <th className="text-left px-3 py-2">Ünvan / BH ID</th>
                    <th className="text-left px-3 py-2">Durum</th>
                    <th className="text-left px-3 py-2">Not</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {(preview.items || []).map((r) => (
                    <tr key={r.bh_id} className="text-slate-300" data-testid={`bh-import-preview-row-${r.bh_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{r.title || r.erp_name || "—"}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{r.bh_id}</div>
                      </td>
                      <td className="px-3 py-2 text-[11px]">
                        {r.status === "new" && <span className="text-emerald-400">Yeni</span>}
                        {r.status === "matched" && <span className="text-blue-400">Eşleşen</span>}
                        {r.status === "already_linked" && <span className="text-slate-400">Zaten Aktarılmış</span>}
                        {r.status === "missing" && <span className="text-amber-400">Eksik</span>}
                        {r.status === "error" && <span className="text-rose-400">Hata</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-400 text-[11px]">{r.reason || r.erp_name || r.tax_no || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPreview(null)} className="rounded-sm">Geri</Button>
              <Button
                data-testid="bh-import-execute-btn"
                onClick={doImport}
                disabled={busy || ((previewCounts.new || 0) + (previewCounts.matched || 0)) === 0}
                className="bg-emerald-600 hover:bg-emerald-500 rounded-sm"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Aktarımı Uygula
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewStat({ label, value, tone }) {
  const toneCls = {
    emerald: "text-emerald-400 border-emerald-900/50 bg-emerald-950/30",
    blue: "text-blue-400 border-blue-900/50 bg-blue-950/30",
    slate: "text-slate-400 border-slate-800 bg-slate-950/40",
    amber: "text-amber-400 border-amber-900/50 bg-amber-950/30",
    rose: "text-rose-400 border-rose-900/50 bg-rose-950/30",
  }[tone] || "text-slate-400 border-slate-800";
  return (
    <div className={`rounded-sm border px-2 py-1.5 ${toneCls}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-mono">{value}</div>
    </div>
  );
}
